/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
==========================================================================================*/

import Vue from 'vue'
import Router from 'vue-router'
import routes from './routes'
import store from '@/store/store'
import { getAccessToken } from '@internetworkexpert/js-common'
import {
  ROLE_ADMIN,
  ROLE_CERTIFICATION_MANAGER,
  ROLE_CONTENT_CREATOR,
} from '@/constants/roles'

Vue.use(Router)

const LOGIN_ROUTE = { name: 'login' }
const HOME_ROUTE = { name: 'home' }
const CERTIFICATIONS_ROUTE = { name: 'certifications' }
const NOT_AUTHORIZED_ROUTE = { name: 'page-not-authorized' }

const redirectToLogin = () => LOGIN_ROUTE

const getRequiredRoles = to =>
  to.matched.flatMap(record => (record.meta && record.meta.requiredRoles) || [])

const getRequiredFeatureFlags = to =>
  to.matched
    .map(record => (record.meta && record.meta.featureFlag) || null)
    .filter(Boolean)

const hydrateToken = async storeInstance => {
  if (storeInstance.state.users.currentUser.token) return true

  const token = getAccessToken()
  if (!token) return false

  try {
    await storeInstance.dispatch('users/setToken', token)
    return Boolean(storeInstance.state.users.currentUser.token)
  } catch (error) {
    return false
  }
}

const ensureUserInfoLoaded = async storeInstance => {
  if (storeInstance.state.users.currentUser.id) return true

  try {
    await storeInstance.dispatch('users/checkUserCompleteInfo')
    return Boolean(storeInstance.state.users.currentUser.id)
  } catch (error) {
    return false
  }
}

const ensureFeatureFlagsLoaded = async (storeInstance, requiredFeatureFlags) => {
  let featureFlagsMap = storeInstance.getters['featureFlags/featureFlagsMap'] || {}

  const missingFlags = requiredFeatureFlags.filter(
    flag => typeof featureFlagsMap[flag] === 'undefined'
  )

  if (!Object.keys(featureFlagsMap).length || missingFlags.length) {
    try {
      await storeInstance.dispatch('featureFlags/initializeDarkly')
      featureFlagsMap = storeInstance.getters['featureFlags/featureFlagsMap'] || {}
    } catch (error) {
      return null
    }
  }

  return featureFlagsMap
}

const hasRequiredRole = (userRoles, requiredRoles) =>
  requiredRoles.some(role => userRoles.includes(role))

const hasRequiredFeatureFlags = (featureFlagsMap, requiredFeatureFlags) =>
  requiredFeatureFlags.every(flag => Boolean(featureFlagsMap[flag]))

const shouldRedirectToCertifications = (to, userRoles) =>
  (to.name === 'home') &&
  userRoles.includes(ROLE_CERTIFICATION_MANAGER) &&
  !userRoles.includes(ROLE_CONTENT_CREATOR) &&
  !userRoles.includes(ROLE_ADMIN)

export const createAuthGuard = storeInstance => async (to, from, next) => {
  const requiredRoles = getRequiredRoles(to)
  const requiredFeatureFlags = getRequiredFeatureFlags(to)

  const isAuthenticated =
    Boolean(storeInstance.state.users.currentUser.token) ||
    (await hydrateToken(storeInstance))

  if (isAuthenticated && to.name === 'login') {
    return next(HOME_ROUTE)
  }

  const requiresAccessControl =
    requiredRoles.length > 0 || requiredFeatureFlags.length > 0

  if (requiresAccessControl && !isAuthenticated) {
    return next(redirectToLogin())
  }

  if (requiredFeatureFlags.length) {
    const hasUserInfo = await ensureUserInfoLoaded(storeInstance)
    if (!hasUserInfo) return next(redirectToLogin())

    const featureFlagsMap = await ensureFeatureFlagsLoaded(
      storeInstance,
      requiredFeatureFlags
    )

    if (!featureFlagsMap) {
      return to.name === 'page-not-authorized'
        ? next()
        : next(NOT_AUTHORIZED_ROUTE)
    }

    if (
      !hasRequiredFeatureFlags(featureFlagsMap, requiredFeatureFlags) &&
      to.name !== 'page-not-authorized'
    ) {
      return next(NOT_AUTHORIZED_ROUTE)
    }
  }

  if (requiredRoles.length) {
    let userRoles = storeInstance.getters['users/currentUserRoles'] || []

    if (!userRoles.length) {
      const hasUserInfo = await ensureUserInfoLoaded(storeInstance)
      if (!hasUserInfo) return next(redirectToLogin())
      userRoles = storeInstance.getters['users/currentUserRoles'] || []
    }

    if (!hasRequiredRole(userRoles, requiredRoles) && to.name !== 'page-not-authorized') {
      if (shouldRedirectToCertifications(to, userRoles)) {
        return next(CERTIFICATIONS_ROUTE)
      }
      return next(NOT_AUTHORIZED_ROUTE)
    }
  }

  return next()
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [...routes],
})

router.beforeResolve(createAuthGuard(store))

router.afterEach(() => {
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
